<template>
  <div class="ai-analysis-content">
    <!-- 主体内容区域 - 简化的三栏布局 -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-4" style="min-height: calc(100vh - 120px)">
      <!-- 左栏：状态信息区 (25%) -->
      <div class="flex flex-col lg:col-span-1 space-y-4">
        <!-- 分析状态卡片 -->
        <div class="card p-4" style="height: 520px">
          <h3
            class="mb-3 flex items-center rounded-t-lg bg-purple-50 px-4 pb-3 pt-4 text-base font-semibold text-gray-800 -mx-4 -mt-4"
          >
            <icon-vue icon="mdi:progress-clock" class="mr-2 text-purple-600" font-size="18px" />
            分析状态
          </h3>
          <div class="flex-1 overflow-auto">
            <analysis-status
              :status="aiAnalysis.status.value"
              :progress="aiAnalysis.progress.value"
              :stage="aiAnalysis.stage.value"
              :message="aiAnalysis.message.value"
              :error="aiAnalysis.error.value"
              :thinking-content="aiAnalysis.aiThinkingCache.displayContent"
              :token-stats="tokenDisplay"
              @view-full-thinking="showThinkingModal = true"
              @retry-analysis="handleRetryAnalysis"
            />
          </div>
        </div>

        <!-- 分析概览卡片 -->
        <div class="card p-4" style="height: 360px">
          <h3
            class="mb-3 flex items-center rounded-t-lg bg-green-50 px-4 pb-3 pt-4 text-base font-semibold text-gray-800 -mx-4 -mt-4"
          >
            <icon-vue icon="mdi:chart-donut" class="animate-breathe mr-2 text-green-600" font-size="18px" />
            分析概览
          </h3>
          <div class="flex-1 overflow-auto">
            <analysis-overview :status="aiAnalysis.status.value" :statistics="analysisSummary" />
          </div>
        </div>
      </div>

      <!-- 中栏：关联分析图谱 (50%) -->
      <div class="flex flex-col lg:col-span-2">
        <div class="card p-4" style="min-height: calc(100vh - 4px)">
          <h3
            class="mb-3 flex items-center rounded-t-lg bg-green-50 px-4 pb-3 pt-4 text-base font-semibold text-gray-800 -mx-4 -mt-4"
          >
            <icon-vue icon="mdi:graph-outline" class="mr-2 text-green-600" font-size="18px" />
            关联分析图谱
          </h3>
          <div class="flex-1 overflow-hidden" style="min-height: 870px">
            <correlation-graph
              :status="aiAnalysis.status.value"
              :graph-data="correlationGraphData"
              :loading="aiAnalysis.isAnalyzing.value"
              :loading-message="graphLoadingMessage"
              :error="aiAnalysis.error.value"
              :height="870"
              @retry="handleRetryAnalysis"
            />
          </div>
        </div>
      </div>

      <!-- 右栏：测试用例区 (25%) -->
      <div class="flex flex-col lg:col-span-1 space-y-4">
        <!-- 最小测试用例集卡片 -->
        <div class="card p-4" style="height: 520px">
          <h3
            class="mb-3 flex items-center rounded-t-lg bg-blue-50 px-4 pb-3 pt-4 text-base font-semibold text-gray-800 -mx-4 -mt-4"
          >
            <icon-vue icon="mdi:shield-check" class="mr-2 text-blue-600" font-size="18px" />
            最小测试用例集
          </h3>
          <div class="overflow-hidden" style="height: calc(100% - 60px)">
            <minimal-test-set
              :status="aiAnalysis.status.value"
              :test-cases="minimalTestCases"
              :loading="aiAnalysis.isAnalyzing.value"
              :loading-message="testSetLoadingMessage"
              :error="aiAnalysis.error.value"
              :statistics="testSetStatistics"
              :selected-cases="selectedTestCases"
              :adopted-cases="adoptedTestCases"
              @retry="handleRetryAnalysis"
              @view-detail="handleViewTestCaseDetail"
              @select-case="handleSelectTestCase"
              @adopt-case="handleAdoptSingleTestCase"
              @batch-adopt="handleBatchAdoptTestCases"
              @adopt-all="handleAdoptAllTestCases"
            />
          </div>
        </div>

        <!-- 覆盖率详情卡片 -->
        <div class="card p-4" style="height: 360px">
          <h3
            class="mb-3 flex items-center rounded-t-lg bg-orange-50 px-4 pb-3 pt-4 text-base font-semibold text-gray-800 -mx-4 -mt-4"
          >
            <icon-vue icon="mdi:chart-timeline-variant" class="mr-2 text-orange-600" font-size="18px" />
            覆盖率详情
          </h3>
          <div class="overflow-hidden" style="height: calc(100% - 60px)">
            <coverage-details
              :status="aiAnalysis.status.value"
              :coverage-data="coverageData"
              :loading="aiAnalysis.isAnalyzing.value"
              :loading-message="coverageLoadingMessage"
              :error="aiAnalysis.error.value"
              @retry="handleRetryAnalysis"
              @view-file-detail="handleViewFileDetail"
              @view-coverage-gaps="handleViewCoverageGaps"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 思考过程模态框 -->
    <a-i-thinking-process
      v-model:visible="showThinkingModal"
      :content="aiAnalysis.aiThinkingCache.fullContent"
      :duration="aiAnalysis.thinkingDuration.value"
      :token-stats="tokenDisplay"
      :chunk-count="aiAnalysis.aiThinkingCache.currentChunkCount"
      :is-content-updating="aiAnalysis.status.value === 'analyzing'"
      @close="showThinkingModal = false"
    />

    <!-- 文件详情模态框 -->
    <file-detail-modal
      v-model:visible="showFileDetailModal"
      :file-detail="selectedFileDetail"
      :coverage-gaps="selectedCoverageGaps"
      @close="showFileDetailModal = false"
    />

    <!-- 覆盖缺口模态框 -->
    <coverage-gaps-modal
      v-model:visible="showCoverageGapsModal"
      :coverage-gaps="selectedGaps"
      @close="showCoverageGapsModal = false"
    />

    <!-- 测试用例详情模态框 -->
    <test-case-detail-modal
      v-model:visible="showTestCaseDetailModal"
      :test-case="selectedTestCase"
      :adopted="selectedTestCase ? adoptedTestCases.includes(selectedTestCase.test_case_id) : false"
      @close="showTestCaseDetailModal = false"
      @adopt="handleAdoptSingleTestCase"
    />
  </div>
</template>

<script setup lang="ts">
import AIThinkingProcess from '@/sites/precision-new/components/ai-analysis/AIThinkingProcess.vue';
import AnalysisOverview from '@/sites/precision-new/components/ai-analysis/AnalysisOverview.vue';
import AnalysisStatus from '@/sites/precision-new/components/ai-analysis/AnalysisStatus.vue';
import CorrelationGraph from '@/sites/precision-new/components/ai-analysis/CorrelationGraph.vue';
import CoverageDetails from '@/sites/precision-new/components/ai-analysis/CoverageDetails.vue';
import CoverageGapsModal from '@/sites/precision-new/components/ai-analysis/CoverageGapsModal.vue';
import FileDetailModal from '@/sites/precision-new/components/ai-analysis/FileDetailModal.vue';
import MinimalTestSet from '@/sites/precision-new/components/ai-analysis/MinimalTestSet.vue';
import TestCaseDetailModal from '@/sites/precision-new/components/ai-analysis/TestCaseDetailModal.vue';
import type { useAIAnalysis } from '@/sites/precision-new/composables/useAIAnalysis';
import type { AIAnalysisTask } from '@/store/modules/ai-analysis-tasks';
import type {
  CorrelationGraphData,
  CoverageEstimation,
  CoverageGap,
  FileCoverageDetail,
  TestCaseCorrelation,
  TestSetStatistics,
} from '@/types/precision-new/ai-analysis';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps<{
  taskId: string;
  task: AIAnalysisTask;
}>();

const emit = defineEmits<{
  (e: 'minimize'): void;
  (e: 'update:task', taskId: string, updates: Partial<AIAnalysisTask>): void;
}>();

// 从父组件注入共享的 AI 分析实例
const aiAnalysis = inject('aiAnalysis') as ReturnType<typeof useAIAnalysis>;

// 模态框状态
const showThinkingModal = ref(false);
const showFileDetailModal = ref(false);
const showCoverageGapsModal = ref(false);
const showTestCaseDetailModal = ref(false);

// 选中的数据
const selectedFileDetail = ref<FileCoverageDetail | null>(null);
const selectedCoverageGaps = ref<CoverageGap[]>([]);
const selectedGaps = ref<CoverageGap[]>([]);
const selectedTestCase = ref<TestCaseCorrelation | null>(null);

// 测试用例采纳相关状态
const selectedTestCases = ref<string[]>([]);
const adoptedTestCases = ref<string[]>([]);

const tokenDisplay = computed(() => {
  const result = aiAnalysis.analysisResult.value;
  return result?.summary?.total_tokens ? `${result.summary.total_tokens}` : '';
});

// 分析概览数据
const analysisSummary = computed(() => {
  const result = aiAnalysis.analysisResult.value;
  if (!result) {
    return {
      totalFiles: '-',
      totalTestCases: '-',
      recommendedTests: '-',
      coverageRate: '-',
    };
  }

  return {
    totalFiles: result.summary?.total_changed_files || '-',
    totalTestCases: result.summary?.total_test_cases || '-',
    recommendedTests: result.summary?.recommended_test_cases || '-',
    coverageRate: result.summary?.estimated_coverage ? `${Math.round(result.summary.estimated_coverage * 100)}%` : '-',
  };
});

// 分析结果数据
const correlationGraphData = computed((): CorrelationGraphData | undefined => {
  const result = aiAnalysis.analysisResult.value?.correlation_graph;
  if (!result) {
    return undefined;
  }
  return {
    nodes: [...result.nodes],
    edges: [...result.edges],
  };
});

const minimalTestCases = computed((): TestCaseCorrelation[] => {
  const result = aiAnalysis.analysisResult.value;
  if (!result?.test_correlations || !result?.minimal_test_set) {
    return [];
  }

  return result.test_correlations
    .map((corr) => ({
      test_case_id: corr.test_case_id,
      test_case_name: corr.test_case_name,
      test_case_summary: corr.test_case_summary,
      test_case_priority: corr.test_case_priority,
      correlation_score: corr.correlation_score,
      functions: corr.functions ? [...corr.functions] : [],
      tags: corr.tags ? [...corr.tags] : undefined,
      estimated_execution_time: corr.estimated_execution_time,
      risk_score: corr.risk_score,
      related_files: corr.related_files ? [...corr.related_files] : undefined,
    }))
    .filter((corr: TestCaseCorrelation) => result.minimal_test_set.includes(corr.test_case_id));
});

const coverageData = computed((): CoverageEstimation | undefined => {
  const result = aiAnalysis.analysisResult.value?.coverage_estimation;
  if (!result) {
    return undefined;
  }
  return {
    estimated_coverage: result.estimated_coverage,
    file_coverage_details: result.file_coverage_details.map((detail) => ({
      file_path: detail.file_path,
      estimated_coverage: detail.estimated_coverage,
      covered_functions: detail.covered_functions ? [...detail.covered_functions] : undefined,
      uncovered_functions: detail.uncovered_functions ? [...detail.uncovered_functions] : undefined,
    })),
    coverage_gaps: result.coverage_gaps?.map((gap) => ({
      file_path: gap.file_path,
      risk_level: gap.risk_level,
      reason: gap.reason,
      functions: gap.functions ? [...gap.functions] : undefined,
    })),
  };
});

const testSetStatistics = computed((): TestSetStatistics | undefined => {
  const result = aiAnalysis.analysisResult.value;
  if (!result) {
    return undefined;
  }

  return {
    totalRecommended: result.minimal_test_set?.length || 0,
    estimatedCoverage: Math.round((result.coverage_estimation?.estimated_coverage || 0) * 100),
    savedTime: 30,
  };
});

// 加载消息
const graphLoadingMessage = computed(() => {
  const stage = aiAnalysis.stage.value;
  if (stage === 'ai_analysis') {
    return 'AI正在分析代码关联...';
  }
  if (stage === 'parsing_result') {
    return '正在生成关联图谱...';
  }
  return '正在分析代码关联...';
});

const testSetLoadingMessage = computed(() => {
  const stage = aiAnalysis.stage.value;
  if (stage === 'ai_thinking') {
    return 'AI正在优化测试集...';
  }
  if (stage === 'ai_analysis') {
    return 'AI正在分析测试关联...';
  }
  return '正在分析测试关联...';
});

const coverageLoadingMessage = computed(() => {
  const stage = aiAnalysis.stage.value;
  if (stage === 'ai_thinking') {
    return 'AI正在生成覆盖报告...';
  }
  if (stage === 'ai_analysis') {
    return 'AI正在计算覆盖率...';
  }
  return '正在计算覆盖率...';
});

// 监听分析状态，同步到任务 Store
watch(
  () => ({
    progress: aiAnalysis.progress.value,
    status: aiAnalysis.status.value,
    stage: aiAnalysis.stage.value,
    message: aiAnalysis.message.value,
    error: aiAnalysis.error.value,
    result: aiAnalysis.analysisResult.value,
  }),
  (state) => {
    emit('update:task', props.taskId, {
      progress: state.progress,
      status: state.status,
      stage: state.stage,
      message: state.message,
      error: state.error || undefined,
      result: state.result ? JSON.parse(JSON.stringify(state.result)) : undefined,
    });
  },
  { deep: true },
);

function handleRetryAnalysis() {
  // 从任务参数中提取分析参数并触发重新分析
  if (props.task.analysisParams) {
    aiAnalysis.startAnalysis(props.task.analysisParams);
  } else {
    message.error('无法重新分析：缺少分析参数');
  }
}

function handleViewTestCaseDetail(testCase: TestCaseCorrelation) {
  selectedTestCase.value = testCase;
  showTestCaseDetailModal.value = true;
}

function handleViewFileDetail(fileDetail: FileCoverageDetail, gaps?: CoverageGap[]) {
  selectedFileDetail.value = fileDetail;
  selectedCoverageGaps.value = gaps || [];
  showFileDetailModal.value = true;
}

function handleViewCoverageGaps(gaps: CoverageGap[]) {
  selectedGaps.value = gaps;
  showCoverageGapsModal.value = true;
}

// Mock API 方法
async function mockAdoptTestCasesToPlan(planId: number, caseIds: string[]) {
  await new Promise((resolve) => setTimeout(resolve, 1200));
  return {
    code: 0,
    message: 'success',
    data: {
      adopted_count: caseIds.length,
      failed_count: 0,
      adopted_cases: caseIds,
    },
  };
}

function handleSelectTestCase(caseId: string, checked: boolean) {
  if (adoptedTestCases.value.includes(caseId)) {
    return;
  }

  if (checked) {
    if (!selectedTestCases.value.includes(caseId)) {
      selectedTestCases.value.push(caseId);
    }
  } else {
    selectedTestCases.value = selectedTestCases.value.filter((id) => id !== caseId);
  }
}

async function handleAdoptSingleTestCase(caseId: string) {
  try {
    const response = await mockAdoptTestCasesToPlan(props.task.planId, [caseId]);

    if (response.code === 0) {
      if (!adoptedTestCases.value.includes(caseId)) {
        adoptedTestCases.value.push(caseId);
      }
      selectedTestCases.value = selectedTestCases.value.filter((id) => id !== caseId);
      message.success('测试用例采纳成功');
    } else {
      message.error('采纳失败，请重试');
    }
  } catch (error) {
    console.error('采纳测试用例失败:', error);
    message.error('采纳失败，请重试');
  }
}

async function handleBatchAdoptTestCases(caseIds: string[]) {
  if (caseIds.length === 0) {
    message.warning('请先选择要采纳的测试用例');
    return;
  }

  try {
    const response = await mockAdoptTestCasesToPlan(props.task.planId, caseIds);

    if (response.code === 0) {
      const newAdoptedCases = caseIds.filter((id) => !adoptedTestCases.value.includes(id));
      adoptedTestCases.value.push(...newAdoptedCases);
      selectedTestCases.value = selectedTestCases.value.filter((id) => !caseIds.includes(id));
      message.success(`成功采纳 ${caseIds.length} 个测试用例`);
    } else {
      message.error('批量采纳失败，请重试');
    }
  } catch (error) {
    console.error('批量采纳失败:', error);
    message.error('批量采纳失败，请重试');
  }
}

async function handleAdoptAllTestCases(caseIds: string[]) {
  if (caseIds.length === 0) {
    message.info('没有可采纳的测试用例');
    return;
  }

  try {
    const response = await mockAdoptTestCasesToPlan(props.task.planId, caseIds);

    if (response.code === 0) {
      const newAdoptedCases = caseIds.filter((id) => !adoptedTestCases.value.includes(id));
      adoptedTestCases.value.push(...newAdoptedCases);
      selectedTestCases.value = [];
      message.success(`成功采纳全部 ${caseIds.length} 个测试用例`);
    } else {
      message.error('全部采纳失败，请重试');
    }
  } catch (error) {
    console.error('全部采纳失败:', error);
    message.error('全部采纳失败，请重试');
  }
}

// 生命周期
onMounted(() => {
  // 其他初始化逻辑可以在这里添加
});

onBeforeUnmount(() => {
  if (aiAnalysis.isAnalyzing.value) {
    aiAnalysis.cancelAnalysis();
  }
});
</script>

<style scoped>
.ai-analysis-content {
  height: 100%;
}

/* ============================================
   优化后的 Header 样式
   ============================================ */
.analysis-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.25);
  position: relative;
  overflow: hidden;
}

.analysis-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
}

/* 左侧：测试计划标题 */
.header-left {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.plan-title {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  min-width: 200px;
}

.plan-icon {
  font-size: 20px;
  color: #ffffff;
  flex-shrink: 0;
}

.plan-name {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 中间：信息卡片组 */
.header-center {
  flex: 1;
  position: relative;
  z-index: 1;
}

.info-cards {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.info-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 140px;
  transition: all 0.3s ease;
}

.info-card:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-label .iconify {
  font-size: 14px;
}

.card-value {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-value.loading {
  display: flex;
  align-items: center;
}

.skeleton {
  height: 16px;
  width: 100px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 分支卡片特殊样式 */
.branch-card {
  min-width: 180px;
}

.branch-value {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'SF Mono', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
}

.branch-tag {
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.3px;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.branch-arrow {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  flex-shrink: 0;
}

/* 右侧：操作按钮 */
.header-right {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.action-button {
  height: 44px;
  padding: 0 24px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  border: none;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.action-button:active {
  transform: translateY(0);
}

.button-icon {
  font-size: 18px;
}

.stop-button .button-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 呼吸动画 */
@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.animate-breathe {
  animation: breathe 3s ease-in-out infinite;
}

/* ============================================
   响应式设计
   ============================================ */
@media (max-width: 1200px) {
  .info-cards {
    gap: 8px;
  }

  .info-card {
    min-width: 120px;
    padding: 8px 12px;
  }

  .branch-tag {
    max-width: 60px;
  }
}

@media (max-width: 768px) {
  .analysis-header {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .header-left,
  .header-center,
  .header-right {
    width: 100%;
  }

  .plan-title {
    justify-content: center;
  }

  .info-cards {
    flex-wrap: wrap;
    justify-content: center;
  }

  .info-card {
    min-width: calc(50% - 6px);
  }

  .branch-card {
    width: 100%;
    min-width: 100%;
  }

  .header-right {
    display: flex;
    justify-content: center;
  }

  .action-button {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}
</style>
